import { SHIP_RADIUS, WORLD_WIDTH, WORLD_HEIGHT } from 'asteroids-shared/constants'
import { wrappedDelta } from 'asteroids-shared/helpers'

// ---- arena / wall bounce -----------------------
export function detectReplayWallBounce(before, after) {
  if (!before?.alive || !after?.alive) return false
  const eps = 2
  const minX = SHIP_RADIUS
  const maxX = WORLD_WIDTH - SHIP_RADIUS
  const minY = SHIP_RADIUS
  const maxY = WORLD_HEIGHT - SHIP_RADIUS
  const bvx0 = before.vx ?? 0
  const bvx1 = after.vx ?? 0
  const bvy0 = before.vy ?? 0
  const bvy1 = after.vy ?? 0
  if ((before.x <= minX + eps || after.x <= minX + eps) && bvx0 < -0.01 && bvx1 > 0.01) return true
  if ((before.x >= maxX - eps || after.x >= maxX - eps) && bvx0 > 0.01 && bvx1 < -0.01) return true
  if ((before.y <= minY + eps || after.y <= minY + eps) && bvy0 < -0.01 && bvy1 > 0.01) return true
  if ((before.y >= maxY - eps || after.y >= maxY - eps) && bvy0 > 0.01 && bvy1 < -0.01) return true
  return false
}

export function distanceFollowToPoint(snap, px, py, o) {
  const ship = o.getFollowShip(snap)
  if (!ship?.alive) return 1400
  const dx = o.toroidalMap ? wrappedDelta(px, ship.x, WORLD_WIDTH) : px - ship.x
  const dy = o.toroidalMap ? wrappedDelta(py, ship.y, WORLD_HEIGHT) : py - ship.y
  return Math.sqrt(dx * dx + dy * dy)
}

export function replayThrustFromInputs(playback, shipId) {
  const steps = playback.appliedSteps
  if (steps <= 0) return false
  const row = playback.decoded.ticks[steps - 1]
  if (!row?.length) return false
  const roster = playback.decoded.rosterIds
  const pi = roster.findIndex(id => String(id) === String(shipId))
  if (pi < 0) return false
  return !!(row[pi]?.up)
}

// ---- per-tick snapshot FX (audio + particles) ----
function emitReplayTickSounds(snapBefore, snapAfter, ctx) {
  const unmuted = !ctx.replayMuted
  const { toroidalMap, audio, particles, getFollowShip } = ctx
  const distOpts = { toroidalMap, getFollowShip }

  const fb = getFollowShip(snapBefore)
  const fa = getFollowShip(snapAfter)
  if (unmuted && !toroidalMap && fb && fa && detectReplayWallBounce(fb, fa)) {
    void audio.resume()
    audio.playWallBoing()
  }

  const beforeAst = new Map((snapBefore.asteroids ?? []).map(a => [a.id, a]))
  const afterIds = new Set((snapAfter.asteroids ?? []).map(a => a.id))
  for (const [, a] of beforeAst) {
    if (!afterIds.has(a.id)) {
      const r = a.radius ?? 20
      if (!ctx.skipAsteroidBurstParticles) {
        particles.spawnAsteroidBurst(a.x, a.y, r)
      }
      if (unmuted) {
        void audio.resume()
        audio.playAsteroidExplosion({
          radius: r,
          distance: distanceFollowToPoint(snapAfter, a.x, a.y, distOpts)
        })
      }
    }
  }

  const msb = snapBefore.modeState
  const msa = snapAfter.modeState
  if (
    unmuted
    && msb?.kind === 'ctf'
    && msa?.kind === 'ctf'
    && msb.teamScores
    && msa.teamScores
  ) {
    const py = msb.teamScores.yellow ?? 0
    const cy = msa.teamScores.yellow ?? 0
    const pc = msb.teamScores.cyan ?? 0
    const cc = msa.teamScores.cyan ?? 0
    if (cy > py || cc > pc) {
      void audio.resume()
      audio.playCtfScoreFanfare()
    }
  }
}

export function emitReplayStepSounds(snapBefore, snapAfter, o) {
  emitReplayTickSounds(snapBefore, snapAfter, {
    replayMuted: o.replayMuted,
    toroidalMap: o.toroidalMap,
    audio: o.audio,
    particles: o.particles,
    getFollowShip: o.getFollowShip,
    skipAsteroidBurstParticles: o.skipAsteroidBurstParticles
  })
  playReplayFirePressSounds({
    replayMuted: o.replayMuted,
    row: o.row,
    followOrder: o.followOrder,
    followIndex: o.followIndex,
    rosterIds: o.rosterIds,
    audio: o.audio
  })
}

// ---- thruster ------------------------------------
export function syncReplayThruster(o) {
  const snap = o.playback.getSnapshot()
  if (!snap) {
    o.audio.setThruster(false)
    return
  }
  const f = o.getFollowShip(snap)
  o.audio.setThruster(!o.replayMuted && !!f?.alive && replayThrustFromInputs(o.playback, f.id))
}

// ---- fire ----------------------------------------
export function playReplayFirePressSounds(o) {
  if (o.replayMuted || !o.row || !o.followOrder.length) return
  const fid = o.followOrder[o.followIndex % o.followOrder.length]?.id
  const pi = o.rosterIds.findIndex(id => String(id) === String(fid))
  if (pi < 0) return
  const fp = o.row[pi]?.firePresses | 0
  if (fp <= 0) return
  void o.audio.resume()
  const n = Math.min(fp, 4)
  for (let i = 0; i < n; i++) {
    o.audio.playBulletShot()
  }
}

// ---- kill explosion ------------------------------
export function playReplayDeathThunder(audio, replayMuted) {
  if (replayMuted) return
  void audio.resume()
  audio.playDeathThunder()
}

